// ============================================================================
// PairsForm — Cloche de notifications in-app
// ============================================================================
// Surface les notifications de l'utilisateur (table notifications_in_app via
// /api/notifications). Badge de non-lues + menu déroulant ; clic sur une
// notification → marque lue + navigue vers son lien. Bouton « tout marquer lu ».
// Placée en position fixe (haut-droite) par ProtectedLayout.
// ============================================================================

import { useCallback, useEffect, useState } from "react";
import {
  Badge, IconButton, Menu, Box, Typography, Button, Divider, CircularProgress, Tooltip,
} from "@mui/material";
import { Bell, CheckCheck, X, RotateCcw, EyeOff } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { formatJourMoisHeureFR } from "@cecodev/shared";

interface NotifItem {
  id: string;
  type: string;
  severite: "INFO" | "WARNING" | "CRITICAL" | string;
  titre: string;
  message: string;
  lienHref: string | null;
  luLe: string | null;
  createdAt: string;
}

type VueNotif = "actives" | "ecartees";

const DOT_COLOR: Record<string, string> = {
  INFO: "#4BB7A5",
  WARNING: "#ed6c02",
  CRITICAL: "#d32f2f",
};

export function NotificationBell() {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [count, setCount] = useState(0);
  const [items, setItems] = useState<NotifItem[] | null>(null);
  const [vue, setVue] = useState<VueNotif>("actives");

  const open = Boolean(anchorEl);

  const loadCount = useCallback(async () => {
    try {
      const r = await api.get<{ count: number }>("/notifications/unread-count");
      setCount(r.data?.count ?? 0);
    } catch {
      /* silencieux */
    }
  }, []);

  // Badge : on rafraîchit le compteur au montage puis toutes les 60 s.
  useEffect(() => {
    void loadCount();
    const id = window.setInterval(() => void loadCount(), 60_000);
    return () => window.clearInterval(id);
  }, [loadCount]);

  const charger = useCallback(async (v: VueNotif) => {
    setItems(null);
    try {
      const url = v === "ecartees" ? "/notifications?masquees=1&limit=30" : "/notifications?limit=30";
      const r = await api.get<NotifItem[]>(url);
      setItems(Array.isArray(r.data) ? r.data : []);
    } catch {
      setItems([]);
    }
  }, []);

  async function ouvrir(e: React.MouseEvent<HTMLElement>) {
    setAnchorEl(e.currentTarget);
    setVue("actives");
    await charger("actives");
  }

  function basculerVue(v: VueNotif) {
    setVue(v);
    void charger(v);
  }

  async function onClickItem(n: NotifItem) {
    setAnchorEl(null);
    if (!n.luLe) {
      try {
        await api.patch(`/notifications/${n.id}/read`);
        void loadCount();
      } catch {
        /* non bloquant */
      }
    }
    if (n.lienHref) navigate(n.lienHref);
  }

  async function ecarter(e: React.MouseEvent, n: NotifItem) {
    e.stopPropagation();
    setItems((prev) => (prev ? prev.filter((x) => x.id !== n.id) : prev));
    try {
      await api.delete(`/notifications/${n.id}`);
      void loadCount();
    } catch {
      void charger(vue); // rollback en rechargeant
    }
  }

  async function restaurer(e: React.MouseEvent, n: NotifItem) {
    e.stopPropagation();
    setItems((prev) => (prev ? prev.filter((x) => x.id !== n.id) : prev));
    try {
      await api.post(`/notifications/${n.id}/restore`);
      void loadCount();
    } catch {
      void charger(vue);
    }
  }

  async function toutMarquerLu() {
    try {
      await api.post("/notifications/read-all");
      setItems((prev) => (prev ? prev.map((n) => ({ ...n, luLe: n.luLe ?? new Date().toISOString() })) : prev));
      setCount(0);
    } catch {
      /* non bloquant */
    }
  }

  return (
    <>
      <Tooltip title="Notifications" arrow>
        <IconButton
          onClick={ouvrir}
          sx={{
            position: "fixed",
            top: 10,
            right: 12,
            zIndex: 1100,
            bgcolor: "background.paper",
            boxShadow: 2,
            "&:hover": { bgcolor: "background.paper" },
          }}
        >
          <Badge badgeContent={count} color="error" max={99}>
            <Bell size={20} />
          </Badge>
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { width: 360, maxWidth: "92vw", maxHeight: 480 } } }}
      >
        <Box sx={{ px: 2, py: 1.5, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {vue === "ecartees" ? "Notifications écartées" : "Notifications"}
          </Typography>
          {vue === "actives" ? (
            <Button size="small" startIcon={<CheckCheck size={14} />} onClick={toutMarquerLu} sx={{ textTransform: "none" }}>
              Tout marquer lu
            </Button>
          ) : (
            <Button size="small" onClick={() => basculerVue("actives")} sx={{ textTransform: "none" }}>
              ← Retour
            </Button>
          )}
        </Box>
        <Divider />

        {items === null ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={22} />
          </Box>
        ) : items.length === 0 ? (
          <Box sx={{ py: 4, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              {vue === "ecartees" ? "Aucune notification écartée." : "Aucune notification."}
            </Typography>
          </Box>
        ) : (
          <Box>
            {items.map((n) => (
              <Box
                key={n.id}
                onClick={() => (vue === "actives" ? onClickItem(n) : undefined)}
                sx={{
                  px: 2,
                  py: 1.25,
                  display: "flex",
                  gap: 1.25,
                  alignItems: "flex-start",
                  cursor: vue === "actives" ? "pointer" : "default",
                  borderLeft: "3px solid",
                  borderLeftColor: n.luLe ? "transparent" : (DOT_COLOR[n.severite] ?? "#4BB7A5"),
                  bgcolor: n.luLe ? "transparent" : "rgba(75,183,165,0.06)",
                  "&:hover": { bgcolor: "action.hover" },
                  borderBottom: "1px solid",
                  borderBottomColor: "divider",
                }}
              >
                <Box
                  sx={{
                    mt: 0.6,
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    flexShrink: 0,
                    bgcolor: DOT_COLOR[n.severite] ?? "#9e9e9e",
                  }}
                />
                <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: n.luLe ? 500 : 700 }}>
                    {n.titre}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                    {n.message}
                  </Typography>
                  <Typography variant="caption" color="text.disabled">
                    {formatJourMoisHeureFR(n.createdAt)}
                  </Typography>
                </Box>
                {vue === "actives" ? (
                  <Tooltip title="Écarter" arrow>
                    <IconButton size="small" onClick={(e) => ecarter(e, n)} sx={{ flexShrink: 0, mt: -0.25 }}>
                      <X size={14} />
                    </IconButton>
                  </Tooltip>
                ) : (
                  <Tooltip title="Restaurer" arrow>
                    <IconButton size="small" onClick={(e) => restaurer(e, n)} sx={{ flexShrink: 0, mt: -0.25 }}>
                      <RotateCcw size={14} />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            ))}
          </Box>
        )}

        {vue === "actives" && (
          <>
            <Divider />
            <Box sx={{ px: 2, py: 1, textAlign: "center" }}>
              <Button
                size="small"
                startIcon={<EyeOff size={13} />}
                onClick={() => basculerVue("ecartees")}
                sx={{ textTransform: "none", color: "text.secondary" }}
              >
                Voir les notifications écartées
              </Button>
            </Box>
          </>
        )}
      </Menu>
    </>
  );
}
